// Bridge to the Android ScreenOrientationPlugin (android/.../ScreenOrientationPlugin.java).
// The bill screen is landscape. The web Screen Orientation API is ignored by the
// Android WebView when the device's system auto-rotate is off, which forced a
// CSS rotate(90deg) fallback that breaks touch-scrolling of the cart list. On
// the native app we instead pin the orientation natively so the device really
// rotates and the layout renders un-rotated (scrolling works normally).

import { Capacitor, registerPlugin } from '@capacitor/core';

interface ScreenOrientationNativePlugin {
  lock(opts: { orientation: 'landscape' | 'portrait' }): Promise<void>;
  unlock(): Promise<void>;
}

const ScreenOrientationNative = registerPlugin<ScreenOrientationNativePlugin>('ScreenOrientation');

const isNativeApp = () => Capacitor.isNativePlatform();

/**
 * Lock the screen to landscape for the duration of a landscape-only screen.
 * On the native Android app this pins the Activity orientation (works even with
 * system auto-rotate off); elsewhere it falls back to the web Screen Orientation
 * API (honoured by desktop fullscreen / supporting browsers). Returns a cleanup
 * function that releases the lock.
 */
export function lockLandscape(): () => void {
  if (isNativeApp()) {
    ScreenOrientationNative.lock({ orientation: 'landscape' }).catch(() => {
      /* plugin missing on an old build — CSS fallback still applies */
    });
    return () => {
      ScreenOrientationNative.unlock().catch(() => {});
    };
  }

  // Web fallback (desktop browser tab / PWA): best-effort web lock.
  const orientation = window.screen?.orientation as
    | (ScreenOrientation & { lock?: (o: string) => Promise<void> })
    | undefined;
  orientation?.lock?.('landscape').catch(() => {
    /* unsupported (desktop browser / no fullscreen) — CSS fallback handles it */
  });
  return () => orientation?.unlock?.();
}
